import React from "react";
import "./SupplierBidCard.scss";
import { Tooltip } from "primereact/tooltip";
import { Dialog } from "primereact/dialog";
import CustomButton from "../../Common/CustomButton/CustomButton";
import { useEffect, useState } from "react";

export interface SupplierBidCardProps {
	supplierName: string;
	sampleQuality: number;
	currentBid: string | number;
	targetBid: string | number;
	gap: {
		price: string | number;
	};
	supplierId?: number | string;
	incoterm?: string;
	onCurrentBidUpdate?: (supplierId: number | string, value: number) => Promise<boolean>;
}

// Custom star rating component with partial fill support
const CustomStarRating: React.FC<{ value: number; maxStars?: number; uniqueId: string }> = ({
	value,
	maxStars = 5,
	uniqueId
}) => {
	const stars = [];
	// Handle null, undefined, or invalid values
	const numericValue = typeof value === 'number' && !isNaN(value) ? value : 0;
	const clampedValue = Math.max(0, Math.min(numericValue, maxStars));

	for (let i = 1; i <= maxStars; i++) {
		const fillPercentage = Math.max(0, Math.min(1, clampedValue - (i - 1))) * 100;

		stars.push(
			<div key={i} className="custom-star-wrapper">
				<svg
					width="24"
					height="24"
					viewBox="0 0 24 24"
					className="custom-star"
				>
					{/* Background (empty) star */}
					<defs>
						<linearGradient id={`star-gradient-${uniqueId}-${i}`}>
							<stop offset={`${fillPercentage}%`} stopColor="var(--color-amber-400)" />
							<stop offset={`${fillPercentage}%`} stopColor="var(--surface-variant)" />
						</linearGradient>
					</defs>
					<path
						d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
						fill={`url(#star-gradient-${uniqueId}-${i})`}
						stroke="var(--color-amber-400)"
						strokeWidth="0.5"
					/>
				</svg>
			</div>
		);
	}

	return <div className={`custom-star-rating custom-star-rating-${uniqueId}`}>{stars}</div>;
};

export const SupplierBidCard: React.FC<SupplierBidCardProps> = ({
	supplierName,
	sampleQuality,
	currentBid,
	targetBid,
	gap,
	supplierId,
	incoterm,
	onCurrentBidUpdate,
}) => {
	// Generate a unique ID for this card (use supplierId or fallback to a random string)
	const uniqueId = supplierId?.toString() || `card-${Math.random().toString(36).substring(2, 11)}`;
	const [editableBid, setEditableBid] = useState<string>(String(currentBid ?? ""));
	const [pendingBid, setPendingBid] = useState<number | null>(null);
	const [showConfirm, setShowConfirm] = useState(false);
	useEffect(() => {
		setEditableBid(String(currentBid ?? ""));
	}, [currentBid]);

	const handleBlur = () => {
		const numericValue = Number(editableBid);

		if (
			!isNaN(numericValue) &&
			supplierId &&
			onCurrentBidUpdate &&
			numericValue !== Number(currentBid)
		) {
			setPendingBid(numericValue);
			setShowConfirm(true);
		}
	};

	const handleBidChange = (value: string) => {
		if (value === "" || /^\d*\.?\d*$/.test(value)) {
			setEditableBid(value);
		}
	};

	const handleCancelUpdate = () => {
		setShowConfirm(false);
		setPendingBid(null);
		setEditableBid(String(currentBid ?? ""));
	};

	const handleConfirmUpdate = async () => {
		if (!supplierId || pendingBid === null || !onCurrentBidUpdate) {
			handleCancelUpdate();
			return;
		}

		const success = await onCurrentBidUpdate(supplierId, pendingBid);
		if (!success) {
			setEditableBid(String(currentBid ?? ""));
		}
		setShowConfirm(false);
		setPendingBid(null);
	};

	// Handle null/undefined sampleQuality values
	const displayQuality = typeof sampleQuality === 'number' && !isNaN(sampleQuality) && sampleQuality > 0
		? sampleQuality
		: 'Missing';

	return (
		<div className="sb-card">
			<div className="sb-container">
				{/* LEFT SIDE */}
				<div className="sb-left">
					<h2 className="sb-title my-0">{supplierName}</h2>

					<div className="sb-cost my-0">Incoterm - {incoterm ?? "-"} </div>

					<div className="sb-quality">
						<div className="sb-cost my-0">Sample Quality</div>
						<Tooltip
							target={`.custom-star-rating-${uniqueId}`}
							content={`Sample Quality Rating: ${displayQuality}`}
							position="bottom"
							className="cursor-pointer"
						/>
						<CustomStarRating value={sampleQuality} uniqueId={uniqueId} />
					</div>
				</div>

				{/* RIGHT SIDE */}
				<div className="sb-right">
					{/* Current Bid + Target */}
					<div className="sb-metric">
						<div className="sb-metric-label">Current Bid</div>
						<div className="sb-metric-value">
							<span className="sb-currency orange">EUR</span>
							{/* ✅ EDITABLE INPUT */}
							<input
								inputMode="decimal"
								value={editableBid}
								onChange={(e) => handleBidChange(e.target.value)}
								onBlur={handleBlur}
								className="sb-input orange"
							/>
						</div>
					</div>

					{/* Progress + Gap */}
					<div className="sb-metric">
						<div className="sb-metric-label">Target</div>
						<div className="sb-metric-value sb-metric-value-target">
							<span className="sb-currency blue">EUR</span>
							<span className="sb-number blue">{targetBid ?? "-"}</span>
						</div>
						<div className="sb-text">Gap</div>

						<div className="sb-line">
							<span className="sb-text">Price</span>
							<span className="arrow">
								▾
							</span>
							<span>{gap.price ?? "-"}</span>
						</div>
					</div>
				</div>
			</div>
			<Dialog
				header="Update Current Bid"
				visible={showConfirm}
				onHide={handleCancelUpdate}
				modal
				style={{ width: "450px" }}
				footer={
					<div className="flex justify-end gap-2">
						<CustomButton
							label="Cancel"
							variant="outlined"
							iconsOnly={false}
							onClick={handleCancelUpdate}
						/>
						<CustomButton
							label="Update"
							variant="primary"
							iconsOnly={false}
							onClick={handleConfirmUpdate}
						/>
					</div>
				}
			>
				<div className="flex align-items-center gap-3">
					{/* <i
						className="pi pi-exclamation-triangle"
						style={{ fontSize: "2rem", color: "var(--status-warning)" }}
					></i> */}
					<div>
						<p className="m-0">
							Are you sure you want to update the current bid to{" "}
							<strong>{pendingBid}</strong>?
						</p>
						<p className="mt-2 mb-0" style={{ color: "var(--text-muted)", fontSize: "0.875rem" }}>
							This action will update the supplier bid value.
						</p>
					</div>
				</div>
			</Dialog>
		</div>
	);
};

export default SupplierBidCard;
